import React, { useState, useEffect } from 'react';
import {
  FiSettings,
  FiDollarSign,
  FiMail,
  FiGlobe,
  FiSave,
  FiRefreshCw
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    general: {
      siteName: 'Kasbia Stays',
      siteUrl: 'https://kasbiastays.co.uk',
      supportEmail: 'support@kasbiastays.co.uk',
      supportPhone: '+44 20 1234 5678',
      address: 'London, United Kingdom'
    },
    booking: {
      minNights: 1,
      maxNights: 30,
      advanceBookingDays: 365,
      serviceFeePercent: 10,
      cleaningFee: 50,
      cancellationPolicy: 'flexible',
      instantBooking: true
    },
    email: {
      smtpHost: '',
      smtpPort: 587,
      smtpUser: '',
      smtpPass: '',
      senderName: 'Kasbia Stays',
      senderEmail: 'bookings@kasbiastays.co.uk'
    },
    seo: {
      metaTitle: 'Kasbia Stays | Luxury Serviced Apartments in London',
      metaDescription: 'Book premium serviced apartments in London. Fully furnished, flexible stays, and exceptional service.',
      metaKeywords: 'serviced apartments, London accommodation, short stay London'
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await api.getSettings();
      if (data) {
        setSettings(prev => ({
          ...prev,
          ...data
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const styles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700'
    },
    saveBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      background: '#1a3a5c',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    tabs: {
      display: 'flex',
      gap: '4px',
      marginBottom: '24px',
      background: 'white',
      padding: '4px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    tab: {
      padding: '12px 20px',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontWeight: '500',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#555'
    },
    tabActive: {
      background: '#1a3a5c',
      color: 'white'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    section: {
      marginBottom: '32px'
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '1px solid #eee'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontWeight: '500',
      marginBottom: '8px',
      fontSize: '0.875rem',
      color: '#484848'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem',
      minHeight: '100px',
      resize: 'vertical'
    },
    select: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem',
      background: 'white'
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    hint: {
      color: '#555',
      fontSize: '0.75rem',
      marginTop: '4px'
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: FiSettings },
    { id: 'booking', label: 'Booking', icon: FiDollarSign },
    { id: 'email', label: 'Email', icon: FiMail },
    { id: 'seo', label: 'SEO', icon: FiGlobe }
  ];

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <header style={styles.header}>
        <h1 style={styles.title}>Settings</h1>
        <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? <FiRefreshCw className="spin" /> : <FiSave />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </header>

      <div style={styles.tabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.card}>
        {/* General Settings */}
        {activeTab === 'general' && (
          <div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Site Name</label>
              <input
                type="text"
                style={styles.input}
                value={settings.general.siteName}
                onChange={(e) => handleChange('general', 'siteName', e.target.value)}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Site URL</label>
              <input
                type="url"
                style={styles.input}
                value={settings.general.siteUrl}
                onChange={(e) => handleChange('general', 'siteUrl', e.target.value)}
              />
            </div>

            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Support Email</label>
                <input
                  type="email"
                  style={styles.input}
                  value={settings.general.supportEmail}
                  onChange={(e) => handleChange('general', 'supportEmail', e.target.value)}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Support Phone</label>
                <input
                  type="tel"
                  style={styles.input}
                  value={settings.general.supportPhone}
                  onChange={(e) => handleChange('general', 'supportPhone', e.target.value)}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Address</label>
              <input
                type="text"
                style={styles.input}
                value={settings.general.address}
                onChange={(e) => handleChange('general', 'address', e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Booking Settings */}
        {activeTab === 'booking' && (
          <div>
            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Minimum Nights</label>
                <input
                  type="number"
                  style={styles.input}
                  value={settings.booking.minNights}
                  onChange={(e) => handleChange('booking', 'minNights', parseInt(e.target.value))}
                  min="1"
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Maximum Nights</label>
                <input
                  type="number"
                  style={styles.input}
                  value={settings.booking.maxNights}
                  onChange={(e) => handleChange('booking', 'maxNights', parseInt(e.target.value))}
                  min="1"
                />
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Service Fee (%)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={settings.booking.serviceFeePercent}
                  onChange={(e) => handleChange('booking', 'serviceFeePercent', parseInt(e.target.value))}
                  min="0"
                  max="100"
                />
                <small style={styles.hint}>Applied to each booking</small>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Default Cleaning Fee (£)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={settings.booking.cleaningFee}
                  onChange={(e) => handleChange('booking', 'cleaningFee', parseInt(e.target.value))}
                  min="0"
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Advance Booking (days)</label>
              <input
                type="number"
                style={styles.input}
                value={settings.booking.advanceBookingDays}
                onChange={(e) => handleChange('booking', 'advanceBookingDays', parseInt(e.target.value))}
                min="1"
              />
              <small style={styles.hint}>How far in advance guests can book</small>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Cancellation Policy</label>
              <select
                style={styles.select}
                value={settings.booking.cancellationPolicy}
                onChange={(e) => handleChange('booking', 'cancellationPolicy', e.target.value)}
              >
                <option value="flexible">Flexible - Free cancellation until 24h before</option>
                <option value="moderate">Moderate - Free cancellation until 5 days before</option>
                <option value="strict">Strict - 50% refund until 7 days before</option>
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={settings.booking.instantBooking}
                  onChange={(e) => handleChange('booking', 'instantBooking', e.target.checked)}
                />
                Enable Instant Booking
              </label>
              <small style={styles.hint}>Allow guests to book without approval</small>
            </div>
          </div>
        )}

        {/* Email Settings */}
        {activeTab === 'email' && (
          <div>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>SMTP Configuration</h3>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>SMTP Host</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={settings.email.smtpHost}
                    onChange={(e) => handleChange('email', 'smtpHost', e.target.value)}
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>SMTP Port</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={settings.email.smtpPort}
                    onChange={(e) => handleChange('email', 'smtpPort', parseInt(e.target.value))}
                  />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>SMTP Username</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={settings.email.smtpUser}
                    onChange={(e) => handleChange('email', 'smtpUser', e.target.value)}
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>SMTP Password</label>
                  <input
                    type="password"
                    style={styles.input}
                    value={settings.email.smtpPass}
                    onChange={(e) => handleChange('email', 'smtpPass', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Sender Information</h3>

              <div style={styles.row}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Sender Name</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={settings.email.senderName}
                    onChange={(e) => handleChange('email', 'senderName', e.target.value)}
                  />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Sender Email</label>
                  <input
                    type="email"
                    style={styles.input}
                    value={settings.email.senderEmail}
                    onChange={(e) => handleChange('email', 'senderEmail', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SEO Settings */}
        {activeTab === 'seo' && (
          <div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Meta Title</label>
              <input
                type="text"
                style={styles.input}
                value={settings.seo.metaTitle}
                onChange={(e) => handleChange('seo', 'metaTitle', e.target.value)}
              />
              <small style={styles.hint}>{settings.seo.metaTitle.length}/60 characters</small>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Meta Description</label>
              <textarea
                style={styles.textarea}
                value={settings.seo.metaDescription}
                onChange={(e) => handleChange('seo', 'metaDescription', e.target.value)}
              />
              <small style={styles.hint}>{settings.seo.metaDescription.length}/160 characters</small>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Meta Keywords</label>
              <input
                type="text"
                style={styles.input}
                value={settings.seo.metaKeywords}
                onChange={(e) => handleChange('seo', 'metaKeywords', e.target.value)}
                placeholder="Separate keywords with commas"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
